import { Controller } from "@hotwired/stimulus"
import { openChrome, closeChrome, bottomNavBridge } from "../helpers/overlay_chrome"

// The streak +1 moment (budgets/_streak_up).
//
// Raised once, on the dashboard render that follows the day's first streak
// action (application_controller#bank_streak_day! sets the flash that renders
// the card). Replaces the old in-strip mascot flip.
//
// Shell copied from review_prompt_controller: the same raised-attribute guard
// against a restored Turbo snapshot re-raising a card the server has already
// consumed, the same scroll lock, and the same native chrome hand-off (tab bar
// away, nav bar dimmed to the scrim's colour) through helpers/overlay_chrome.
//
// The choreography, once the card has landed:
//
//   0.00s  embers start rising off the flame
//   0.42s  the mascot springs up over the top edge of the card
//   0.90s  the number rolls up one row (yesterday's count -> today's)
//   1.48s  it lands: flare, flash, shockwave, ember confetti, success
//          haptic, then the "current streak" label
//   1.70s  this week's row fades in
//   2.10s  today's dot catches fire
//   2.45s  title, subline and the CTA rise in, staggered
//
// prefers-reduced-motion skips straight to the landed state.
export default class extends Controller {
  static targets = ["panel", "haptic", "mascot", "hero", "flame", "embers", "odometer", "column", "today", "reveal"]

  // Mirrors review_prompt_controller.RAISED_ATTRIBUTE: stamped on the card
  // when it is raised, so a Turbo snapshot carries it and a restore does not
  // open the card again.
  static RAISED_ATTRIBUTE = "data-streak-up-raised"

  // The old in-strip farewell waited this long after connect before playing:
  // long enough for Turbo to finish painting and, on the native refresh that
  // follows an expense saved in a modal, for that modal to finish dismissing.
  // Any sooner and the opening plays behind the modal, unseen.
  static SHOW_DELAY = 600

  // Ember emitters, ported from streak_flame_controller (the native chip's
  // 16px flame) and scaled up 9x for the 150px hero flame.
  static EMBER_SCALE = 9
  static EMITTERS = [
    { diameter: 4.0, offset:  0.0, jitter: [-1.2, 1.2], drift: [-2.5,  2.5], rise: [14, 19], duration: [1.30, 1.70], peak: [0.95, 1.10], interval: [1.4, 2.2] },
    { diameter: 2.4, offset: -2.5, jitter: [-0.8, 0.4], drift: [-3.5, -1.0], rise: [ 9, 13], duration: [1.05, 1.40], peak: [0.80, 1.00], interval: [1.2, 2.0] },
    { diameter: 2.4, offset:  2.5, jitter: [-0.4, 0.8], drift: [ 1.0,  3.5], rise: [ 9, 13], duration: [1.05, 1.40], peak: [0.80, 1.00], interval: [1.2, 2.0] }
  ]

  // Same amber palette as the streak page's tap-to-celebrate burst
  // (streak_celebration_controller#spawnConfetti), so the burst reads as fire.
  static CONFETTI_PALETTE = ["#f59e0b", "#fb923c", "#fbbf24", "#f97316", "#fde68a", "#facc15"]

  static EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)"
  static EASE_SPRING = "cubic-bezier(0.2, 1.55, 0.35, 1)"

  connect() {
    this._timers = []
    this._embersStopped = false

    // Turbo caches the DOM as it stands. Put the card away before the snapshot
    // is taken so a restore never paints a half-played celebration.
    this._resetForCache = () => this._reset()
    document.addEventListener("turbo:before-cache", this._resetForCache)

    // Already raised in this DOM: a restored snapshot re-runs connect() on a
    // card whose flash the server has long since consumed.
    if (this.element.hasAttribute(this.constructor.RAISED_ATTRIBUTE)) return

    // The tab bar goes now, not with the card: every dashboard render rebuilds
    // the native bar with its own slide-in (bridge--bottom-nav connect, sent a
    // frame after it connects), and a hide held for SHOW_DELAY lets it land
    // first, then pulls it straight back down. Two frames puts this hide just
    // behind that connect, before the slide-in's delay is up.
    requestAnimationFrame(() => requestAnimationFrame(() => bottomNavBridge(this)?.hide()))
    this._timers.push(setTimeout(() => this.show(), this.constructor.SHOW_DELAY))
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this._resetForCache)
    this._clearTimers()
    this._stopEmbers()
    this._unlockScroll()
  }

  show() {
    this.element.setAttribute(this.constructor.RAISED_ATTRIBUTE, "1")
    this.panelTarget.hidden = false
    this._lockScroll()
    this._applyChrome()
    // Same double-rAF re-assert the celebration popups use (see
    // welcome_celebration#_open): the budget carousel calls bridge.show() for
    // its first slide during load, which would clobber an immediate hide().
    requestAnimationFrame(() => requestAnimationFrame(() => this._applyChrome()))

    if (this._reducedMotion) {
      this._landInstantly()
      return
    }

    this._play()
  }

  _applyChrome() {
    openChrome(this, this.element)
  }

  // ── choreography ──────────────────────────────────────────────

  // Disney-principles pass: the scrim leads the card, every beat gets its own
  // moment (staging), the digit dips before it rolls (anticipation), the hero
  // and flame land with squash and stretch, the flame trails the hero by 60ms
  // (follow-through), and the mascot cheers a beat after the number lands
  // (secondary action). The whole sequence lands ~180ms sooner than before.
  _play() {
    this._fadeInScrim()
    this._startEmbers()
    this._at(300, () => this._leapMascot())
    this._at(680, () => this._crouchDigit())
    this._at(830, () => this._roll())
    this._at(1300, () => this._land())
    this._at(1480, () => this._dealWeek())
    this._at(2020, () => this._igniteToday())
    this._at(2300, () => this._revealCopy())
  }

  _fadeInScrim() {
    const scrim = this.element.querySelector(".streak-up-scrim")
    scrim?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: "ease-out" })
  }

  // Leaps in on an arc from below and to the side, stretching on the way up
  // and squashing as it lands. Mirrored under RTL.
  _leapMascot() {
    if (!this.hasMascotTarget) return
    const side = this._rtl ? -1 : 1
    this.mascotTarget.animate(
      [
        { transform: `translate(${-26 * side}px, 46px) scale(0.8, 0.7) rotate(${-8 * side}deg)`, opacity: 0 },
        { transform: `translate(${-8 * side}px, -18px) scale(0.93, 1.1) rotate(${-2 * side}deg)`, opacity: 1, offset: 0.45 },
        { transform: "translate(0, 3px) scale(1.1, 0.9)", offset: 0.72 },
        { transform: "translate(0, -1px) scale(0.98, 1.02)", offset: 0.88 },
        { transform: "none", opacity: 1 }
      ],
      { duration: 640, easing: "cubic-bezier(0.3, 0.6, 0.4, 1)", fill: "forwards" }
    )
  }

  // The mascot reacts to the number landing: a dip, a hop, a settle.
  _cheerMascot() {
    if (!this.hasMascotTarget) return
    const side = this._rtl ? -1 : 1
    this.mascotTarget.animate(
      [
        { transform: "none" },
        { transform: "translateY(2px) scale(1.06, 0.94)", offset: 0.18 },
        { transform: `translateY(-12px) scale(0.95, 1.07) rotate(${-3 * side}deg)`, offset: 0.45 },
        { transform: "translateY(1px) scale(1.05, 0.95)", offset: 0.75 },
        { transform: "none" }
      ],
      { duration: 560, delay: 90, easing: "ease-out" }
    )
  }

  // Anticipation: the digit sinks and squashes a little before it rolls.
  _crouchDigit() {
    this.columnTarget.animate(
      [{ transform: "translateY(0) scaleY(1)" }, { transform: "translateY(8px) scaleY(0.94)" }],
      { duration: 150, easing: "cubic-bezier(0.4, 0, 0.6, 1)", fill: "forwards" }
    )
  }

  // The column slides up one row, so today's count replaces yesterday's. It
  // overshoots the row by ~10%, dips back past it, and settles.
  _roll() {
    const height = this.odometerTarget.offsetHeight
    this.columnTarget.animate(
      [
        { transform: "translateY(8px) scaleY(0.94)" },
        { transform: `translateY(${-height * 1.096}px) scaleY(1.06)`, offset: 0.62 },
        { transform: `translateY(${-height * 0.971}px) scaleY(0.98)`, offset: 0.84 },
        { transform: `translateY(${-height}px) scaleY(1)` }
      ],
      { duration: 620, easing: "cubic-bezier(0.3, 0, 0.25, 1)", fill: "forwards" }
    )
  }

  _land() {
    this._fireHaptic()
    this.heroTarget.animate(
      [
        { transform: "scale(1)" },
        { transform: "scale(1.18, 0.84)", offset: 0.2 },
        { transform: "scale(0.94, 1.1)", offset: 0.5 },
        { transform: "scale(1.03, 0.98)", offset: 0.76 },
        { transform: "scale(1)" }
      ],
      { duration: 640, easing: "ease-out" }
    )
    // The flame's resting transform is its centring; it trails the hero by
    // 60ms and stretches the other way.
    const centred = getComputedStyle(this.flameTarget).transform
    this.flameTarget.animate(
      [
        { transform: `${centred} scale(1)` },
        { transform: `${centred} scale(0.9, 1.3)`, offset: 0.32 },
        { transform: `${centred} scale(1.08, 0.94)`, offset: 0.62 },
        { transform: `${centred} scale(1)` }
      ],
      { duration: 700, delay: 60, easing: "ease-out" }
    )
    this._flash(this.heroTarget)
    this._shock(this.heroTarget, 4, 620, { settle: true })
    this._confetti(this.heroTarget)
    this._cheerMascot()
    this._revealRow(0)
  }

  // The week row arrives one dot after another, then the kept run ripples
  // toward today.
  _dealWeek() {
    const week = this.revealTargets[1]
    if (!week) return
    week.style.opacity = "1"
    const days = [...week.children]
    days.forEach((day, i) => day.animate(
      [{ opacity: 0, transform: "translateY(10px) scale(0.8)" }, { opacity: 1, transform: "none" }],
      { duration: 360, delay: i * 35, easing: this.constructor.EASE_OUT, fill: "backwards" }
    ))
    week.querySelectorAll(".streak-up-dot.is-done").forEach((dot, i) => dot.animate(
      [{ transform: "scale(1)" }, { transform: "scale(1.18, 0.88)", offset: 0.4 }, { transform: "scale(1)" }],
      { duration: 300, delay: 300 + i * 60, easing: "ease-out" }
    ))
  }

  // Today crouches, then catches fire with a tall stretch and a squash on the
  // way down.
  _igniteToday() {
    if (!this.hasTodayTarget) return
    const dot = this.todayTarget
    dot.animate(
      [{ transform: "scale(1)" }, { transform: "scale(0.72, 0.8)" }],
      { duration: 110, easing: "ease-in", fill: "forwards" }
    ).onfinish = () => {
      dot.classList.add("is-fire")
      dot.innerHTML = this._flameIcon()
      dot.animate(
        [
          { transform: "scale(0.72, 0.8)" },
          { transform: "scale(1, 1.42) translateY(-3px)", offset: 0.35 },
          { transform: "scale(1.15, 0.9)", offset: 0.65 },
          { transform: "scale(1)" }
        ],
        { duration: 520, easing: "ease-out", fill: "forwards" }
      )
      this._shock(dot, 2.6, 460, { settle: true })
      this._confetti(dot, { count: 8, spread: 0.6 })
    }
  }

  // reveal targets, in order: label, week row, title, subline, CTA.
  _revealRow(index) {
    const el = this.revealTargets[index]
    if (el) this._rise(el, 0)
  }

  _revealCopy() {
    this.revealTargets.slice(2).forEach((el, i) => this._rise(el, i * 70))
  }

  _rise(el, delay) {
    el.animate(
      [{ opacity: 0, transform: "translateY(8px)" }, { opacity: 1, transform: "translateY(0)" }],
      { duration: 440, delay, easing: this.constructor.EASE_OUT, fill: "forwards" }
    )
  }

  // Reduced motion: everything in its landed state, nothing moving.
  _landInstantly() {
    if (this.hasMascotTarget) { this.mascotTarget.style.transform = "none"; this.mascotTarget.style.opacity = "1" }
    this.columnTarget.firstElementChild?.remove()
    if (this.hasTodayTarget) {
      this.todayTarget.classList.add("is-fire")
      this.todayTarget.innerHTML = this._flameIcon()
    }
    this.revealTargets.forEach((el) => { el.style.opacity = "1" })
  }

  // ── effects ───────────────────────────────────────────────────

  _startEmbers() {
    this.constructor.EMITTERS.forEach((cfg, i) => {
      this._timers.push(setTimeout(() => this._emit(cfg), (0.2 + 0.4 * i + Math.random() * 0.3) * 1000))
    })
  }

  _stopEmbers() {
    this._embersStopped = true
    if (this.hasEmbersTarget) this.embersTarget.innerHTML = ""
  }

  _emit(cfg) {
    if (this._embersStopped || !this.hasEmbersTarget) return

    const scale = this.constructor.EMBER_SCALE
    const x0 = (cfg.offset + this._rand(cfg.jitter)) * scale
    const drift = this._rand(cfg.drift) * scale
    const rise = this._rand(cfg.rise) * scale
    const peak = this._rand(cfg.peak)
    const xMid = x0 + drift * 0.45
    const xEnd = x0 + drift
    const size = cfg.diameter * scale

    const ember = document.createElement("span")
    ember.className = "streak-up-ember"
    ember.style.width = `${size}px`
    ember.style.height = `${size}px`
    ember.style.marginInlineStart = `${-size / 2}px`
    this.embersTarget.appendChild(ember)

    const anim = ember.animate(
      [
        { transform: `translate(${x0}px, 0) scale(0.55)`, opacity: 0, offset: 0 },
        { transform: `translate(${x0}px, ${-rise * 0.18}px) scale(0.55)`, opacity: 1, offset: 0.2 },
        { transform: `translate(${xMid}px, ${-rise * 0.4}px) scale(${peak})`, opacity: 1, offset: 0.35 },
        { transform: `translate(${xMid}px, ${-rise * 0.62}px) scale(${peak})`, opacity: 0.85, offset: 0.55 },
        { transform: `translate(${xEnd}px, ${-rise * 0.8}px) scale(${peak * 0.5})`, opacity: 0.5, offset: 0.75 },
        { transform: `translate(${xEnd}px, ${-rise}px) scale(0.25)`, opacity: 0, offset: 1 }
      ],
      { duration: this._rand(cfg.duration) * 1000, easing: "cubic-bezier(0.2, 0.6, 0.2, 1)", fill: "forwards" }
    )
    anim.onfinish = () => {
      ember.remove()
      this._timers.push(setTimeout(() => this._emit(cfg), this._rand(cfg.interval) * 1000))
    }
  }

  _flash(host) {
    const flash = document.createElement("span")
    flash.className = "streak-up-flash"
    host.appendChild(flash)
    flash.animate(
      [{ transform: "scale(0)", opacity: 0 }, { transform: "scale(2.25)", opacity: 1, offset: 0.4 }, { transform: "scale(3)", opacity: 0 }],
      { duration: 320, easing: "cubic-bezier(0.2, 0.7, 0.3, 1)", fill: "forwards" }
    ).onfinish = () => flash.remove()
  }

  _shock(host, to, duration, { settle = false } = {}) {
    const ring = document.createElement("span")
    ring.className = "streak-up-shock"
    host.appendChild(ring)
    ring.animate(
      [{ transform: "scale(0.4)", opacity: 0 }, { transform: "scale(2.5)", opacity: 0.9, offset: 0.2 }, { transform: `scale(${to})`, opacity: 0 }],
      { duration, easing: settle ? "cubic-bezier(0.1, 0.8, 0.2, 1)" : "cubic-bezier(0.25, 0.6, 0.2, 1)", fill: "forwards" }
    ).onfinish = () => ring.remove()
  }

  // Lightweight DOM confetti, the streak page's technique: GPU-only
  // transforms, every piece removed when its animation finishes. Pieces
  // launch upward and fall on a gravity arc, flipping as they go, instead of
  // flying out in straight lines.
  _confetti(host, { count = 18, spread = 1 } = {}) {
    const palette = this.constructor.CONFETTI_PALETTE
    const GRAVITY = 360
    const SAMPLES = 10

    for (let i = 0; i < count; i++) {
      const piece = document.createElement("span")
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 2.2 * spread
      const speed = 120 + Math.random() * 90
      const vx = Math.cos(angle) * speed
      const vy = Math.sin(angle) * speed
      const spin = (Math.random() - 0.5) * 720
      const size = 5 + Math.random() * 6
      const isCircle = Math.random() < 0.35

      piece.style.cssText = `
        position: absolute;
        inset-inline-start: 50%;
        inset-block-start: 50%;
        width: ${size}px;
        height: ${size}px;
        background: ${palette[i % palette.length]};
        border-radius: ${isCircle ? "50%" : "2px"};
        pointer-events: none;
        will-change: transform, opacity;
        transform: translate(-50%, -50%);
      `
      host.appendChild(piece)

      const keyframes = []
      for (let k = 0; k <= SAMPLES; k++) {
        const t = (k / SAMPLES) * 1.1
        const x = vx * t
        const y = vy * t + 0.5 * GRAVITY * t * t
        const flip = Math.cos(t * 14 + i).toFixed(2)
        keyframes.push({
          transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) rotate(${spin * t}deg) scale(${flip}, 1)`,
          opacity: k < 7 ? 1 : 1 - (k - 7) / 3
        })
      }
      piece.animate(keyframes, { duration: 1100 + Math.random() * 250, easing: "linear", fill: "forwards" })
        .onfinish = () => piece.remove()
    }
  }

  _flameIcon() {
    return '<svg viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>'
  }

  // ── closing ───────────────────────────────────────────────────

  // The CTA. A stay-put close that hands the dashboard back, so it announces
  // (the rating ask waits on this, see review_prompt_controller#connect).
  dismiss() {
    this._close()
    document.dispatchEvent(new CustomEvent("celebration:closed"))
  }

  _close() {
    this._clearTimers()
    this._stopEmbers()
    this.panelTarget.hidden = true
    this._unlockScroll()
    closeChrome(this)
  }

  // turbo:before-cache: hidden, unlocked, and still marked raised.
  _reset() {
    this._clearTimers()
    this._stopEmbers()
    this.panelTarget.hidden = true
    this._unlockScroll()
  }

  // ── helpers ───────────────────────────────────────────────────

  _at(ms, fn) {
    this._timers.push(setTimeout(fn, ms))
  }

  _clearTimers() {
    this._timers.forEach((t) => clearTimeout(t))
    this._timers = []
  }

  _rand([min, max]) {
    return min + Math.random() * (max - min)
  }

  get _rtl() {
    return document.documentElement.getAttribute("dir") === "rtl"
  }

  get _reducedMotion() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  _fireHaptic() {
    if (!this.hasHapticTarget) return
    const haptic = this.application.getControllerForElementAndIdentifier(this.hapticTarget, "bridge--haptic")
    haptic?.tap()
  }

  _lockScroll() {
    this._prevOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = "hidden"
  }

  // Only the lock this card took is released (see review_prompt_controller).
  _unlockScroll() {
    if (this._prevOverflow === undefined) return

    document.documentElement.style.overflow = this._prevOverflow
    this._prevOverflow = undefined
  }
}
